<template>
    <section class="players-games-section" v-if="currentGame !== undefined">
        <div class="section-title">
            <div class="section-title__line"></div>
            <div class="section-title__title">GAMES</div>
            <div class="section-title__line"></div>
        </div>

        <template v-if="isGridView">
          <p v-if="!player.is_authorized" class="games-grid__login-message">
            <a href="" @click.prevent="onGameSectionLoginClick">Login</a> to Participate
          </p>

          <div class="games-grid">
            <div
                class="games-grid__tile"
                v-for="game in gamesChunked"
                :key="game.name"
                @click="onGridTileClick(game)"
            >
              <img :src="game.banner_file" />
            </div>
          </div>
        </template>

        <div
            class="games-container"
            v-else
            :class="{'one-game-mode': isOneGameMode, 'one-slide-swiper': isOneSlideSwiperMode(gamesChunked)}"
        >
          <div class="main-game">
            <template v-if="isGameRunnerVisible">
              <!-- on mobile GameRunner already renders a link to the game with
                   its own play icon, so the intermediate button below would only
                   cost an extra tap. On desktop it is needed: the game iframe is
                   loaded on click, not on page load -->
              <GameRunner
                  v-if="isGameStarted || isMobileDevice"
                  :banner="currentGame.banner_file"
                  :languages="currentGame.languages"
                  :gameUrl="currentGame.game_url"
                  :lang="this.$route.query.lang || 'en'"
                  :open-in-new-tab="true"
              />
              <div class="banner banner_playable" v-else>
                <img :src="currentGame.banner_file" />
                <button
                    type="button"
                    class="banner__play-button"
                    aria-label="Play"
                    @click="onPlayButtonClick"
                >
                  <img src="@/assets/img/tournaments/demo-stand/play-button-v2.svg" alt="play" />
                </button>
              </div>
            </template>

            <div class="banner" v-else>
              <img :src="currentGame.banner_file"/>
              <span v-if="!player.is_authorized && isTournamentPlayable">
                <a href="" @click.prevent="onGameSectionLoginClick">Login</a> to Participate
              </span>
              <span v-if="!isTournamentPlayable">
                Current campaign has been finished. <br />
                You may find more information about our products at <a :href="siteUrl">Kendoo</a>.<br/>
                Until the next time!
              </span>
            </div>

          </div>

          <div class="games-slider" v-if="isGamesSliderVisible">
            <div class="swiper" ref="swiper">
              <div class="swiper-wrapper">
                <div
                    v-for="game in gamesChunked"
                    class="swiper-slide"
                >
                  <img
                      :src="game.banner_file"
                      :class="{'active': game.name === currentGame.name}"
                      @click.prevent="onGameBannerClick(game)"
                  />
                  </div>
              </div>
              <div class="swiper-scrollbar" ref="swiperScrollbar"></div>
            </div>
          </div>

        </div>
    </section>
</template>

<script>

import GameRunner from "@/components/game_runner";
import Swiper from "@/assets/js/Swiper";
import {settings} from "@/lib/settings";
import {isMobile} from "@/lib/browser";
import {nextTick} from "vue";

const TOURNAMENTS_PLAYABLE_STATUSES = ['NEW', 'PRE_NOTIFICATION', 'STARTED'];

export default {
    name: "TournamentPlayersGamesSection",
    components: {
        GameRunner,
    },
    props: {
        player: Object,
        tournament: Object,
    },

    emits: ['onLoginClick'],

    data() {
        return {
            currentGame: undefined,
            isGameStarted: false,
            isMobileDevice: !!isMobile.any(),
            isGridView: true,
            swiperOneRowMode: false,
        }
    },

    watch: {
        'tournament.main_game'() {
            this.initCurrentGame();
        },

        'tournament.games'() {
            this.initSlider();
        },
    },

    computed: {
        siteUrl() {
            const {protocol, port} = window.location;
            const portPostfix = port === '80' ? `:${port}` : '';
            return `${protocol}//${settings.SERVICE_NAME}${portPostfix}`;
        },

        isGameRunnerVisible() {
            if (!this.player.is_authorized) {
                return false;
            }
            return this.isTournamentPlayable;
        },

        isTournamentPlayable() {
            const tournamentStatus = this.tournament.status;
            return TOURNAMENTS_PLAYABLE_STATUSES.indexOf(tournamentStatus) >= 0;
        },

        isOneGameMode() {
            const tGames = this.tournament?.games || [];
            return tGames.length === 1;
        },

        isGamesSliderVisible() {
            const tournamentGames = this.tournament?.games || [];
            return tournamentGames.length > 1;
        },

        gamesChunked() {
            const result = [];
            const tGames = [...this.tournament?.games] || [];
            // mock
            // if (tGames.length) {
            //     for (let l = 0; l < 30; l++) {
            //         tGames.push(tGames[l]);
            //     }
            // }
            return tGames;

            // this.swiperOneRowMode = this.isOneRowSwiperMode(tGames)

            // if (this.swiperOneRowMode) {
            //     for (let i = 0; i <= tGames.length - 1; i++) {
            //         result.push([tGames[i]]);
            //     }
            // } else {
            //     for (let i = 1, ri = 0; i <= tGames.length; i++) {
            //         if (result.length < ri + 1) {
            //             result.push([]);
            //         }
            //         result[ri].push(tGames[i - 1]);
            //         if (i % 3 === 0) {
            //             ri++;
            //         }
            //     }
            // }
            // return result;
        },
    },

    mounted() {
        this.initCurrentGame();
        this.initSlider();
    },

    methods: {

        initCurrentGame() {
            if (this.currentGame === undefined) {
                const mainGame = this.getMainGame();
                this.setCurrentGame(mainGame);
            }
        },

        getMainGame() {
            const tnmt = this.tournament || {};
            let game = tnmt['main_game'];
            if (game) {
                return game;
            }

            const tGames = tnmt.games || [];
            if (tGames.length) {
                game = tGames[0];
            }
            return game;
        },

        onGameBannerClick(game) {
            if (this.currentGame.name === game.name) {
                return;
            }
            this.setCurrentGame(game);
        },

        setCurrentGame(game) {
            this.currentGame = game;
            this.isGameStarted = false;
        },

        onGameSectionLoginClick() {
            this.$emit('onLoginClick');
            window.scrollTo({top: 0, behavior: 'smooth'});
        },

        onGridTileClick(game) {
            this.setCurrentGame(game);
            this.isGridView = false;
            this.$nextTick(() => this.initSlider());
        },

        onPlayButtonClick() {
            this.isGameStarted = true;
        },

        isOneRowSwiperMode(gamesList) {
            // return window.innerWidth <= 992 && gamesList.length < 9;
            return true;
        },

        isOneSlideSwiperMode(gamesList) {
            return gamesList.length === 1;
        },

        // No resize handling on purpose. Rebuilding the slider on window
        // resize used to throw the carousel back to the first slide: on mobile
        // the browser fires resize while hiding the address bar during a normal
        // vertical scroll. Swiper recalculates slide sizes and breakpoints on
        // resize itself and keeps the current position.
        initSlider() {
            if (this.swiper) {
                this.swiper.destroy();
            }
            if (this.isGridView || !this.isGamesSliderVisible) {
                return;
            }
            nextTick(() => {
                this.swiper = new Swiper(
                    this.$refs.swiper,
                    {
                        observer: true,
                        observeParents: true,
                        slidesPerView: 2.3,
                        spaceBetween: 8,
                        simulateTouch: true,
                        grabCursor: true,
                        // autoHeight: true,
                        // calculateHeight: true,
                        scrollbar: {
                            el: this.$refs.swiperScrollbar,
                            draggable: false,
                            // dragSize: dragSize
                        },
                        breakpoints: {
                            // 360: {
                            //     spaceBetween: 10,
                            //     slidesPerView: 3,
                            // },
                            // 480: {
                            //     spaceBetween: 12,
                            //     slidesPerView: 3.35,
                            // },
                            // 641: {
                            //     spaceBetween: 16,
                            //     slidesPerView: 4.4,
                            // },

                            768: {
                                spaceBetween: 16,
                                slidesPerView: 3.5,
                            },

                            993: {
                                spaceBetween: 16,
                                slidesPerView: 5,
                            },
                            1366: {
                                spaceBetween: 24,
                                slidesPerView: 5,
                            },
                        }
                    }
                );
            });
        },
    }
}
</script>

<style scoped>
</style>